import React from "react";
import { Link } from "react-router-dom";
import {
  CarFront,
  Phone,
  Mail,
  ArrowRight,
  Users,
  ShieldCheck,
  Trophy,
  MapPin,
  Handshake,
  Quote,
} from "lucide-react";
import { CtaBand } from "@/components/CtaBand";
import { BuyingJourney } from "@/components/BuyingJourney";
import { PurposeSection } from "@/components/PurposeSection";
import { AboutStory } from "@/components/AboutStory";
import { mailLink } from "@/config";

const About: React.FC = () => {
  return (
    <>
      <section className="about-page-hero">
        <div className="container-x about-page-hero-inner">
          <div className="about-page-hero-grid">
            <div className="about-page-hero-copy">
              <p className="why-work-label"><span />Your Car Buying Partner</p>
              <h1>Meet Simphiwe<br /><span>Fudwana</span></h1>
              <h2>Pre-Owned Vehicle Sales Consultant at Toyota Tokai</h2>
              <p>A great car buying experience starts with the right person beside you. Simphiwe Fudwana brings a people-first approach to every customer journey, helping you navigate your options with honest guidance, product knowledge, and a clear understanding of what matters most to you.</p>
              <p>Whether you’re buying your first car, upgrading to something more spacious, or looking for a reliable everyday vehicle, Simphiwe is here to make the process feel simpler and more reassuring. From first enquiry to final handover, the focus is always on personal attention, straightforward advice, and a smoother path to the right car.</p>
              <div className="about-page-hero-actions">
                <Link to="/vehicles/"><CarFront />Browse Vehicles<ArrowRight /></Link>
                <Link to="/contact/"><Phone />Get In Touch<ArrowRight /></Link>
                <a href={mailLink()}><Mail />Email Me<ArrowRight /></a>
              </div>
              <div className="about-page-hero-trust">
                {[
                  { Icon: Users, title: "People First", text: "Real conversations. Real support." },
                  { Icon: ShieldCheck, title: "Honest Advice", text: "Straightforward guidance, always." },
                  { Icon: Handshake, title: "Smooth Handover", text: "A simpler, more reassuring experience." },
                ].map(({ Icon, title, text }) => <div key={title}><Icon aria-hidden="true" /><span><strong>{title}</strong><small>{text}</small></span></div>)}
              </div>
            </div>
            <div className="about-page-hero-visual">
              <img src="/images/about-portrait-enhanced.png" alt="Simphiwe Fudwana at Toyota Tokai" />
              <div className="about-page-hero-motto"><Quote aria-hidden="true" /><span>Good People.<br />Great Journeys.</span></div>
              <blockquote>“It’s not just about cars,<br />it’s about people.”<cite>— &nbsp; Simphiwe Fudwana</cite></blockquote>
            </div>
          </div>
          <div className="about-page-hero-stats">
            {[
              { Icon: Trophy, title: "5+", text: "Years Experience", sub: "In the automotive industry" },
              { Icon: MapPin, title: "Toyota Tokai", text: "Proudly serving Cape Town", sub: "Trusted. Local. People-first." },
              { Icon: Users, title: "Personal Support", text: "From enquiry to handover", sub: "I’m here for your journey." },
            ].map(({ Icon, title, text, sub }) => <div key={title}><Icon aria-hidden="true" /><span><strong>{title}</strong><span>{text}</span><small>{sub}</small></span></div>)}
            <p>Driven<br />by People</p>
          </div>
        </div>
      </section>

      <AboutStory />

      <BuyingJourney />

      <PurposeSection />

      <CtaBand
        eyebrow="Let's talk about your next car"
        title="Get In Touch Today"
        desc="I'm here to help — whether you have a question, want to view a vehicle, check availability, or just need honest guidance. Let's make your next move simple."
      />
    </>
  );
};

export default About;
